import { Autocomplete, TextField, createFilterOptions } from '@mui/material';
import type { AutocompleteProps, TextFieldProps } from '@mui/material';
import { useIntl } from 'react-intl';
import { forwardRef, useState } from 'react';
import type { ReactNode } from 'react';
import AddTeamMemberItem from './AddTeamMemberItem';
import { AddTeamMemberDialog } from './AddTeamMemberDialog';

type Member = {
    readonly user: {
        readonly fullName: string;
        readonly emailAddress: string;
        readonly id: string;
    };
};
type MemberFilterOption = Member & {
    readonly inputValue?: string;
};
const filter = createFilterOptions<MemberFilterOption>();

export type TeamMemberSelectProps = {
    value: Member | null;
    members: ReadonlyArray<Member>;
    required?: boolean;
    label?: ReactNode;
    margin?: TextFieldProps['margin'];
    setValue?(member: Member | null): void;
    onAdd?(user: {
        firstName: string;
        lastName: string;
        email: string;
    }): Promise<void>;

    // Added these to make it work with react hook form
    onChange?(member: Member | null): void;
    onBlur?: AutocompleteProps<Member, false, false, false>['onBlur'];

    error?: TextFieldProps['error'];
    helperText?: TextFieldProps['helperText'];
};

export const TeamMemberSelect = forwardRef<HTMLElement, TeamMemberSelectProps>(
    (props, ref) => {
        const {
            members,
            value,
            setValue,
            onChange,
            onBlur,
            label,
            margin = 'none',
            required = false,
            error,
            helperText,
        } = props;
        const { formatMessage } = useIntl();
        const [isAddTeamMemberDialogOpen, toggleAddTeamMemberDialog] =
            useState(false);

        return (
            <>
                <Autocomplete<MemberFilterOption, false, false, true>
                    freeSolo
                    forcePopupIcon
                    clearOnBlur
                    clearOnEscape
                    ref={ref}
                    renderInput={(props) => (
                        <TextField
                            required={required}
                            {...props}
                            inputProps={{
                                ...props.inputProps,

                                // We have added this in the theme for MuiTextField, not sure why it's not added
                                // automatically here
                                autoComplete: 'off',
                                // Prevent lastpass
                                'data-lpignore': 'true',
                            }}
                            error={error}
                            helperText={helperText}
                            margin={margin}
                            label={
                                label ??
                                formatMessage({
                                    defaultMessage: 'Name or Email',
                                })
                            }
                        />
                    )}
                    value={value}
                    options={members}
                    onChange={(_event, value /*, reason*/) => {
                        // Hitting enter produces this. Perhaps we should show the team member dialog?
                        if (typeof value === 'string') {
                            toggleAddTeamMemberDialog(true);
                            setValue?.(null);
                            onChange?.(null);
                        } else if (value?.inputValue !== undefined) {
                            toggleAddTeamMemberDialog(true);
                            setValue?.(null);
                            onChange?.(null);
                        } else {
                            // Create a new value from the user input
                            setValue?.(value);
                            onChange?.(value);
                        }
                    }}
                    renderOption={({ key, ...props }, option /*, state */) => {
                        return (
                            <li
                                key={option.inputValue ? 'new' : option.user.id}
                                {...props}
                            >
                                {option.inputValue ? (
                                    <AddTeamMemberItem />
                                ) : (
                                    `${option.user.fullName} <${option.user.emailAddress}>`
                                )}
                            </li>
                        );
                    }}
                    getOptionLabel={(option) => {
                        // Value selected with enter, right from the input
                        if (typeof option === 'string') {
                            return option;
                        }
                        // Add "xxx" option created dynamically
                        if (option.inputValue) {
                            return option.inputValue;
                        }
                        // Regular option
                        return `${option.user.fullName} <${option.user.emailAddress}>`;
                    }}
                    onBlur={onBlur}
                    filterOptions={(options, params) => {
                        const filtered = filter(options, params);

                        const { inputValue } = params;
                        // Suggest the creation of a new value
                        const isExisting = options.some(
                            (option) =>
                                inputValue === option.user.fullName ||
                                inputValue === option.user.emailAddress,
                        );
                        if (inputValue !== '' && !isExisting) {
                            filtered.push({
                                inputValue,
                                user: {
                                    id: '',
                                    fullName: '',
                                    emailAddress: '',
                                },
                            });
                        }
                        return filtered;
                    }}
                />
                <AddTeamMemberDialog
                    open={isAddTeamMemberDialogOpen}
                    close={() => toggleAddTeamMemberDialog(false)}
                    onSubmit={props.onAdd}
                />
            </>
        );
    },
);
